/**
 * "Jump to date" — the daily log's way off the current week
 * (design: "Led AI Console.dc.html", the DAILY LOGS block).
 *
 * The week strip moves seven days at a time. Getting to a walk from six weeks
 * ago meant six clicks on the back arrow, each one a round trip to the
 * server, with no way to see whether the day you were heading for had
 * anything on it until you arrived. The design answers that with a pill at
 * the right end of the day row that opens a month.
 *
 * ## shadcn's anatomy, not a new widget
 *
 * shadcn's date picker is a COMPOSITION — Popover → PopoverTrigger →
 * PopoverContent → `<Calendar mode="single" selected onSelect />` — rather
 * than a component of its own, and that is the shape here: the trigger is the
 * design's pill, the popover is `AnchoredPanel`, and the grid is the
 * console's one `Calendar`. Nothing about a month is re-implemented; this
 * file is the trigger, the panel and the legend under it.
 *
 * `AnchoredPanel` rather than the design's `position:absolute` — that
 * absolute panel is precisely the bug `AnchoredPanel` was written to remove
 * (a dropdown clipped by an ancestor's `overflow`), and the daily log's
 * header sits inside a scrolling pane.
 *
 * ## The dots are the reason it beats a stepper
 *
 * Every day that logged something gets a dot: green where a walk happened,
 * amber where entries are still waiting to be reviewed. So the month answers
 * "which day am I looking for" before you click, instead of after — and the
 * amber dots make the backlog visible as a shape across the month, which no
 * count in a header does.
 *
 * They cost nothing to draw. `getDailyLog` builds its `days` array from the
 * UNRANGED item read, so the page already holds a count for every day ever
 * logged in scope; the calendar is reading what the week strip beside it is
 * already reading.
 *
 * ## "Today" clears the parameter
 *
 * It does not write today's date. A link that pinned today would still say
 * today tomorrow, and this page gets sent to people — the same rule the week
 * strip's today cell follows.
 */

import { useRef, useState } from "react";
import type { DailyLogDayRow } from "../api";
import { C, FONT_MONO } from "../tokens";
import { AnchoredPanel } from "./AnchoredPanel";
import { Calendar, isoDay } from "./Calendar";
import { META } from "./chrome";

export interface JumpToDateProps {
  /** The day on screen, `YYYY-MM-DD`. */
  day: string;
  today: Date;
  /** Every day in scope that logged something — the same array the week
   *  strip reads, so the two cannot disagree about which days are empty. */
  days: DailyLogDayRow[];
  /** `null` clears `?day=` — see the file header on Today. */
  onPick: (key: string | null) => void;
}

export function JumpToDate({ day, today, days, onPick }: JumpToDateProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const todayKey = isoDay(today);

  const byDay = new Map(days.map((entry) => [entry.day, entry]));
  /** Amber outranks green: a day whose entries are still pending is the one
   *  worth going to, and a day can be both. */
  const markerFor = (key: string): string | undefined => {
    const entry = byDay.get(key);
    if (!entry || entry.entries === 0) return undefined;
    return entry.pendingReview > 0 ? C.amber : C.green;
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="kb"
        data-testid="jump-to-date"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          height: 36,
          padding: "0 12px",
          borderRadius: 999,
          background: C.surface,
          border: `1px solid ${open ? C.line2 : C.hair}`,
          color: C.ink,
          fontSize: 14.95,
          fontWeight: 500,
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        <CalendarGlyph />
        Jump to date
        <Chevron open={open} />
      </button>

      {open && (
        <AnchoredPanel
          anchorRef={triggerRef}
          onClose={() => setOpen(false)}
          label="Jump to a date"
          role="dialog"
          minWidth={262}
          maxWidth={300}
          testId="jump-to-date-panel"
          panelStyle={{
            padding: 2,
            borderRadius: 14,
            border: `1px solid ${C.popLine}`,
            background: C.surface,
            boxShadow: `0 1px 2px ${C.softShadow}, 0 14px 36px ${C.popShadow}`,
          }}
        >
          <Calendar
            testId="jump-cal"
            value={day}
            today={today}
            /* The log's own footer replaces the calendar's: there is no
               "Clear" here, because the report is always showing SOME day —
               and the legend explaining the dots belongs to the daily log,
               not to a calendar that must stay ignorant of walks. */
            footer={false}
            markerFor={markerFor}
            /* No log exists for tomorrow — nor a month of them. */
            maxDay={todayKey}
            maxMonth={todayKey.slice(0, 7)}
            onSelect={(next) => {
              if (!next) return;
              setOpen(false);
              onPick(next === todayKey ? null : next);
            }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              margin: "0 12px",
              padding: "8px 0 4px",
              borderTop: `1px solid ${C.railEdge}`,
            }}
          >
            <LegendDot tone={C.green} label="walked" />
            <LegendDot tone={C.amber} label="to review" />
            <span style={{ flex: 1 }} />
            <button
              type="button"
              className="kb"
              data-testid="jump-to-today"
              onClick={() => {
                setOpen(false);
                onPick(null);
              }}
              style={{
                background: "transparent",
                border: "none",
                padding: "2px 1px",
                font: "inherit",
                fontSize: 14.37,
                fontWeight: 600,
                color: C.goldInk,
                cursor: "pointer",
              }}
            >
              Today
            </button>
          </div>
        </AnchoredPanel>
      )}
    </>
  );
}

function LegendDot({ tone, label }: { tone: string; label: string }) {
  return (
    <span
      style={{
        ...META,
        display: "flex",
        alignItems: "center",
        gap: 5,
        fontSize: 10.35,
        fontFamily: FONT_MONO,
        color: C.propLabel,
      }}
    >
      <span
        aria-hidden
        style={{ width: 5, height: 5, borderRadius: 3, background: tone }}
      />
      {label}
    </span>
  );
}

function CalendarGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" stroke={C.sub2} strokeWidth="1.8" />
      <path
        d="M3.5 10h17M8.5 3.5V6M15.5 3.5V6"
        stroke={C.sub2}
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 10 6"
      fill="none"
      aria-hidden
      style={{ transform: open ? "rotate(180deg)" : undefined, transition: "transform 120ms" }}
    >
      <path
        d="M1 1l4 4 4-4"
        stroke={C.sub2}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
